import React, { useState } from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import {
  Modal,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  Switch,
  Textarea,
  Input,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useStyles } from "@/hooks/useStyles";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import { useQueryClient } from "@tanstack/react-query";

import { MdOutlineAutoMode } from "react-icons/md";
import { CgUnavailable } from "react-icons/cg";
import addUserImgModal from "../../../adduser-modal.png";

type FormValues = {
  status_automation: string;
  customer_instruction: string;
  customer_group_id: string;
  customer_group_alias: string;
};

export default function AddCustomerGroupModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const styles = useStyles();
  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid },
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      status_automation: "pending_review",
      customer_instruction: "",
      customer_group_id: "",
      customer_group_alias: "",
    },
  });
  const [loading] = useState(false);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    await axiosPrivate.post(`/customers_group/settings`, data);

    reset();
    onClose();
    queryClient.invalidateQueries({ queryKey: ["CustomerGroupSettings"] });
  };

  if (loading) {
    return <div>{t("loading")}</div>;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        reset();
        onClose();
      }}
      backdrop="opaque"
      placement="top-center"
      size="2xl"
      classNames={{
        backdrop:
          "bg-gradient-to-t from-vistadark/80 to-vistadark/60 backdrop-opacity-20",
      }}
    >
      <ModalContent
        as="form"
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
      >
        <ModalBody>
          <div className="flex pt-5">
            <div>
              <div className="text-2xl font-bold pt-3 pb-6">
                {t("add_customer_group_settings")}
              </div>
              <div className="text-vistagreen">
                {t("fill_required_info_for_customer_settings")}
              </div>
            </div>
            <img
              src={addUserImgModal}
              alt={t("alt_vistaflow_customer_settings")}
              className="w-48 origin-left self-center"
            />
          </div>

          <div className="space-y-6">
            <Controller
              name="customer_group_id"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  label={t("id")}
                  placeholder={t("id")}
                  variant="flat"
                  labelPlacement="outside"
                  classNames={styles}
                  isRequired
                />
              )}
            />

            <Controller
              name="customer_group_alias"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  label={t("alias")}
                  placeholder={t("alias")}
                  variant="flat"
                  labelPlacement="outside"
                  classNames={styles}
                  isRequired
                />
              )}
            />

            <Controller
              name="status_automation"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Switch
                  size="md"
                  color="primary"
                  startContent={<MdOutlineAutoMode />}
                  endContent={<CgUnavailable />}
                  isSelected={value === "auto"}
                  onValueChange={(isSelected) =>
                    onChange(isSelected ? "auto" : "pending_review")
                  }
                >
                  {t("mode_auto")}
                </Switch>
              )}
            />

            <Controller
              name="customer_instruction"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  label={t("instructions")}
                  placeholder={t("enter_your_instructions")}
                  classNames={styles}
                />
              )}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="default" variant="flat" onPress={onClose}>
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            disabled={!isValid}
            className="bg-vistagreen text-white disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("add_customer_group_settings")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
